import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Loader2, Check, Play, RotateCcw, Sparkles, Volume2, VolumeX, Upload, Film } from 'lucide-react';
import { toast } from '../utils/toast';
import AnchorFrameIterator from './AnchorFrameIterator';
const API = window.__API_BASE + '/api';
const resolveUrl = (url) => !url ? '' : url.startsWith('http') ? url : `${window.__API_BASE}${url}`;

const ENGINES = [
  // 2026-07-01 — Two Video-Edit engines now live in the workbench:
  //   • Runway Aleph → routed to Runway's `aleph2` model
  //     (POST /v1/video_to_video) via services/runway_service. Uses paid
  //     Runway credits, best for identity-critical edits (cast/wardrobe).
  //   • Gemini Omni → routed to Google's `gemini-omni-flash-preview` via
  //     services/gemini_omni_flash. Uses our GEMINI_OMNI_KEYS
  //     round-robin, no Runway credits. Better for stylistic /
  //     lighting / color edits; visual identity locking is weaker.
  // Both require an existing shot video (video-to-video only).
  { id: 'runway',     label: 'Runway Aleph', color: '#A78BFA', bg: 'rgba(167,139,250,0.15)', border: 'rgba(167,139,250,0.4)', premium: true },
  { id: 'omni-flash', label: 'Gemini Omni',  color: '#60A5FA', bg: 'rgba(96,165,250,0.15)',  border: 'rgba(96,165,250,0.4)',  premium: true },
  { id: 'kling',      label: 'Kling',      color: '#F43F5E', bg: 'rgba(244,63,94,0.15)', border: 'rgba(244,63,94,0.4)', premium: true },
  { id: 'veo',        label: 'Veo',        color: '#34D399', bg: 'rgba(52,211,153,0.15)', border: 'rgba(52,211,153,0.4)', premium: false },
  { id: 'seedance',   label: 'Seedance',   color: '#22D3EE', bg: 'rgba(34,211,238,0.15)', border: 'rgba(34,211,238,0.4)', premium: false },
];

export default function VideoWorkbench({ shotId, shot, project, assetBible, isPremium, onClose, onAccept }) {
  // Default engine: Aleph is only valid when the shot already has a source
  // video to edit. For brand-new shots we default to Seedance (Veo for
  // non-premium operators) so the operator can generate a base clip first.
  const hasSourceVideo = !!shot?.video_url;
  const [engine, setEngine] = useState(
    isPremium && hasSourceVideo ? 'runway' : (isPremium ? 'seedance' : 'veo')
  );
  const [availableEngines, setAvailableEngines] = useState({});

  // Common settings
  const [prompt, setPrompt] = useState(shot?.keyframe_prompt || shot?.i2v_prompt || shot?.description || '');
  const [duration, setDuration] = useState(() => {
    const raw = shot?.duration || 5;
    return Math.max(3, Math.min(15, raw));
  });
  const [sound, setSound] = useState(false);
  const [cfgScale, setCfgScale] = useState(0.5);
  const [mode, setMode] = useState('pro');
  const [useCharacterLock, setUseCharacterLock] = useState(true);

  // Kling motion control
  const [motionFile, setMotionFile] = useState(null);
  const [motionPreview, setMotionPreview] = useState(null);
  const motionInputRef = useRef(null);

  // GoogleOmni reference images (up to 2 operator uploads — keyframe occupies
  // ref slot 0 server-side, so the operator can stack 2 more for a total of 3
  // refs which is Aleph's documented cap).
  const [omniRefs, setOmniRefs] = useState([]); // [{ file, preview }]
  const omniRefInputRef = useRef(null);

  // State
  const [generating, setGenerating] = useState(false);
  const [iterations, setIterations] = useState([]);
  // selectedVideoUrl: the iteration currently shown in the main canvas
  // and highlighted in the list. Rules (refined 2026-07):
  //   • Auto-promoted ONLY when a new iteration first arrives (fresh
  //     generation → user actually SEES what they triggered).
  //   • Subsequent poll ticks do NOT yank selection — side-by-side
  //     R0/R1/R2 comparison stays intact.
  //   • Explicit user click always wins and sticks.
  //   • Null means "show R0 as the active baseline" on first mount.
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
  // r0VideoUrl: the IMMUTABLE original generation. Returned by the
  // iterations API. Stays correct even after the canvas-level video_url
  // has been promoted to an accepted iteration.
  const [r0VideoUrl, setR0VideoUrl] = useState(null);
  const [r0Engine, setR0Engine] = useState('');
  // ── Anchor frame state (2026-02 new feature) ─────────────────────────
  // anchorR0Url: PNG of frame 0 of the source video, auto-extracted on
  // first open (gpt-image-2-friendly). anchorIterations: stack of edits
  // the operator makes via the AnchorFrameIterator panel. selectedAnchor
  // is what gets sent to Aleph as the primary identity anchor.
  const [anchorR0Url, setAnchorR0Url] = useState(null);
  const [anchorIterations, setAnchorIterations] = useState([]);
  const [selectedAnchorUrl, setSelectedAnchorUrl] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const videoRef = useRef(null);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const keyframeUrl = shot?.keyframe_s3_url || shot?.keyframe_url;
  const castCandidates = assetBible?.cast_candidates?.filter(c => c.url || c.mugshot_url) || [];
  const engineMeta = ENGINES.find(e => e.id === engine) || ENGINES[0];

  // Fetch engine availability + existing iterations
  useEffect(() => {
    (async () => {
      try {
        const [engRes, iterRes] = await Promise.all([
          axios.get(`${API}/video-workbench/engines`).catch(() => null),
          axios.get(`${API}/shots/${shotId}/video-workbench-iterations`).catch(() => null),
        ]);
        if (engRes?.data) setAvailableEngines(engRes.data);
        if (iterRes?.data) {
          setIterations(iterRes.data.iterations || []);
          setGenerating(iterRes.data.generating || false);
          // Capture the immutable R0 reference (set server-side on first
          // accept; falls back to the live video_url when nothing has been
          // accepted yet).
          if (iterRes.data.r0_video_url) setR0VideoUrl(iterRes.data.r0_video_url);
          if (iterRes.data.r0_engine) setR0Engine(iterRes.data.r0_engine);
          // DO NOT auto-select the latest iteration on mount. Default
          // selection = null so R0 is the implicit active baseline. The
          // user explicitly clicks a card to "move it to panel".
          if (iterRes.data.generating) startPolling();
        }
      } catch { /* */ }
      setLoading(false);
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [shotId]);

  // ── Anchor frame fetch + auto-extract ───────────────────────────────
  // Runs only when engine === 'runway' (Aleph is the only engine that
  // consumes anchor frames). On first open we GET the current state; if
  // R0 hasn't been extracted yet, we kick the extract endpoint. Both
  // calls are idempotent server-side, so re-mounts are safe.
  useEffect(() => {
    if (engine !== 'runway') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/shots/${shotId}/anchor-frame`);
        if (cancelled) return;
        const data = res.data || {};
        if (data.iterations) setAnchorIterations(data.iterations);
        if (data.r0_url) {
          setAnchorR0Url(data.r0_url);
          // If operator hasn't picked one yet, default to R0 as the anchor.
          // It's harmless if they don't generate Aleph — the URL is just
          // queued for the next generate call.
          setSelectedAnchorUrl((prev) => prev || data.r0_url);
        } else {
          // No R0 extracted yet — trigger extract. Server-side ffmpeg
          // pulls frame 0 of shot.video_url_r0 (or video_url) → S3.
          try {
            const ex = await axios.post(`${API}/shots/${shotId}/anchor-frame/extract`);
            if (!cancelled && ex.data?.r0_url) {
              setAnchorR0Url(ex.data.r0_url);
              setSelectedAnchorUrl((prev) => prev || ex.data.r0_url);
            }
          } catch {
            /* shot may not have a source video yet — silent */
          }
        }
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [shotId, engine]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/shots/${shotId}/video-workbench-iterations`);
        const d = res.data;
        const nextIters = d.iterations || [];
        // Detect newly-arrived iterations. Feb 2026 UX: auto-select the
        // freshest one when it lands so the user actually SEES what they
        // just triggered without hunting for the card. Explicit clicks
        // afterwards still stick — side-by-side R0/R1/R2 comparison
        // remains fully functional (see comment below).
        setIterations(prev => {
          if (nextIters.length > (prev?.length || 0)) {
            const newest = nextIters[nextIters.length - 1];
            if (newest?.video_url) setSelectedVideoUrl(newest.video_url);
            // Feb 2026 UX — fresh iteration lands → scroll the panel to
            // top so the newest card (rendered first thanks to reverse())
            // is guaranteed above the fold. Uses next-tick so React has
            // committed the new DOM node before we measure.
            setTimeout(() => {
              if (scrollRef.current) scrollRef.current.scrollTop = 0;
            }, 60);
          }
          return nextIters;
        });
        setGenerating(d.generating || false);
        if (d.r0_video_url) setR0VideoUrl(d.r0_video_url);
        if (d.r0_engine) setR0Engine(d.r0_engine);
        if (d.error) { setError(d.error); setGenerating(false); }
        // CRITICAL (2026-02 fix, refined 2026-07): the poll no longer
        // yanks selection on EVERY tick — that broke side-by-side
        // comparison of R0/R1/R2. We only promote when iterations grow
        // (fresh generation arriving), and explicit clicks always win.
        if (!d.generating && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch { /* */ }
    }, 5000);
  };

  const handleGenerate = async () => {
    if (!keyframeUrl) { toast.error('No keyframe available'); return; }

    setGenerating(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('engine', engine);
      formData.append('prompt', prompt);
      formData.append('duration', duration);
      formData.append('mode', mode);
      formData.append('sound', sound);
      formData.append('cfg_scale', cfgScale);
      formData.append('use_character_lock', useCharacterLock);
      if (engine === 'kling' && motionFile) {
        formData.append('motion_video', motionFile);
      }
      if (isV2V) {
        // Optional style / identity refs (up to 3). Aleph 2 receives them
        // as keyframes[{uri, seconds:0}]; Omni Flash folds them into the
        // prompt as visual descriptors (its Interactions input list only
        // has one video + text slot).
        if (omniRefs[0]) formData.append('ref_image_1', omniRefs[0].file);
        if (omniRefs[1]) formData.append('ref_image_2', omniRefs[1].file);
        if (omniRefs[2]) formData.append('ref_image_3', omniRefs[2].file);
        // Anchor frame URL — only Aleph uses this today (server prepends
        // as dominant identity anchor). Harmless for Omni Flash.
        if (selectedAnchorUrl) formData.append('anchor_frame_url', selectedAnchorUrl);
      }

      await axios.post(
        `${API}/shots/${shotId}/video-workbench-generate`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.info(`${engineMeta.label} is generating your video...`);
      startPolling();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Generation failed';
      setError(msg);
      setGenerating(false);
      toast.error(msg);
    }
  };

  const handleAccept = async () => {
    if (!selectedVideoUrl) return;
    try {
      // Resolve the engine label for the picked video. Iterations carry
      // their own ``engine`` field; R0 uses the original ``r0Engine``
      // (so promoting R0 back to the canvas restores the correct
      // ``video_engine`` instead of mis-labelling it as the workbench's
      // current engine, e.g. "runway").
      const r0Url = r0VideoUrl || shot?.video_url;
      const isR0 = selectedVideoUrl === r0Url;
      const selIter = !isR0 ? iterations.find(i => i.video_url === selectedVideoUrl) : null;
      const resolvedEngine = isR0
        ? (r0Engine || shot?.video_engine || engine)
        : (selIter?.engine || engine);

      await axios.post(`${API}/shots/${shotId}/video-workbench-accept`, {
        video_url: selectedVideoUrl,
        engine: resolvedEngine,
      });
      toast.success(isR0 ? 'R0 restored to panel' : 'Video accepted and moved to panel');
      onAccept?.(selectedVideoUrl);
      onClose();
    } catch {
      toast.error('Failed to accept video');
    }
  };

  const handleRevert = async () => {
    try {
      await axios.post(`${API}/shots/${shotId}/video-workbench-revert`);
      setIterations([]);
      setSelectedVideoUrl(null);
      setError('');
      toast.success('All workbench iterations cleared');
    } catch {
      toast.error('Failed to revert');
    }
  };

  const handleMotionFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMotionFile(f);
    setMotionPreview(URL.createObjectURL(f));
  };

  const canGenerate = !generating && !!keyframeUrl;
  const latestVideo = selectedVideoUrl || (iterations.length > 0 ? iterations[iterations.length - 1]?.video_url : null);

  // Duration constraints per engine
  // Video-to-video engines: Runway Aleph 2 + Gemini Omni Flash. Both edit
  // an existing shot's video (require `hasSourceVideo`), both render the
  // SOURCE VIDEO pane on the left instead of the SOURCE KEYFRAME thumb.
  // Everything else in the workbench (kling/veo/seedance) is image-to-video.
  const isV2V = engine === 'runway' || engine === 'omni-flash';

  // Runway Aleph 2 caps at 5s (only the first 5s of the source video are used).
  // Gemini Omni Flash caps at 10s (Google's public preview cap — model can
  // technically do longer but rollout throttles it).
  const durationRange = engine === 'veo' ? { min: 4, max: 8, step: 2 }
    : engine === 'seedance' ? { min: 5, max: 10, step: 5 }
    : engine === 'kling' ? { min: 5, max: 10, step: 5 }
    : engine === 'runway' ? { min: 3, max: 5, step: 1 }
    : engine === 'omni-flash' ? { min: 3, max: 10, step: 1 }
    : { min: 3, max: 15, step: 1 }; // legacy happyhorse fallback

  // Clamp duration when engine changes
  useEffect(() => {
    const range = engine === 'veo' ? { min: 4, max: 8 }
      : engine === 'seedance' ? { min: 5, max: 10 }
      : engine === 'kling' ? { min: 5, max: 10 }
      : engine === 'runway' ? { min: 3, max: 5 }
      : engine === 'omni-flash' ? { min: 3, max: 10 }
      : { min: 3, max: 15 };
    setDuration(prev => {
      const clamped = Math.max(range.min, Math.min(range.max, prev));
      return clamped;
    });
  }, [engine]);

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-0 md:p-8"
      style={{ background: 'rgba(0,0,0,0.92)' }}
      onClick={onClose}
      data-testid="video-workbench-modal"
    >
      <div
        className="relative w-full max-w-6xl h-[95vh] md:max-h-[90vh] rounded-none md:rounded-2xl overflow-hidden flex flex-col"
        style={{ background: '#0a1520', border: `1px solid ${engineMeta.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-sm font-bold text-white flex items-center gap-2" data-testid="video-workbench-title">
              <Film className="w-4 h-4" style={{ color: engineMeta.color }} />
              Video Generation Workbench
              <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider"
                style={{ background: engineMeta.bg, color: engineMeta.color }}>
                {engineMeta.label}
              </span>
            </h2>
            <p className="text-[10px] text-white/40 mt-0.5">
              {shot?.scene_number} — {shot?.shot_number} · Generate cinematic video from keyframe
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="video-workbench-close-btn">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
          {/* ═══ LEFT: Keyframe + Generated Videos ═══ */}
          <div className="flex-1 overflow-y-auto p-3 md:p-4 min-h-0" ref={scrollRef}>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: engineMeta.color }} />
              </div>
            ) : (
              <>
                {generating && (
                  <div className="flex items-center gap-2 p-3 rounded-xl border mb-3"
                    style={{ borderColor: engineMeta.border, background: engineMeta.bg }}>
                    <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" style={{ color: engineMeta.color }} />
                    <span className="text-[11px] font-medium" style={{ color: engineMeta.color }}>
                      {engineMeta.label} is generating your video...
                    </span>
                  </div>
                )}

                {/* Source Video — shown only when on Runway Aleph AND the
                 *  shot already has a locked / accepted video (the one being
                 *  edited). For other engines the workbench is a generate-
                 *  from-keyframe flow, so this pane is Runway-specific.
                 *
                 *  Runway Aleph requires a source video — it reads it as
                 *  `videoUri` and only uses the FIRST 5 seconds. The
                 *  keyframe is also sent as a reference image to anchor
                 *  cast / wardrobe identity.
                 *
                 *  R0 = the ORIGINAL generation, immutable. The backend
                 *  preserves it in ``shot.video_url_r0`` on the first
                 *  accept; until then ``r0_video_url`` falls back to
                 *  ``shot.video_url``. This guarantees the user can ALWAYS
                 *  go back to R0 even after promoting an iteration to the
                 *  canvas.
                 *
                 *  2026-02 enhancement: R0 is now a SELECTABLE card just
                 *  like the iterations below it. Clicking R0 highlights
                 *  it and enables "Accept Video" — so the user can
                 *  promote R0 back onto the canvas if they previously
                 *  accepted an iteration and want to roll back. */}
                {isV2V && (r0VideoUrl || shot?.video_url) && (() => {
                  const r0Url = r0VideoUrl || shot.video_url;
                  const isR0Selected = selectedVideoUrl === r0Url;
                  // Engine-tinted chrome so operators can tell at a glance
                  // whether the pane is bound to Aleph (violet) or Omni
                  // Flash (blue).
                  const paneColor = engine === 'omni-flash' ? '#60A5FA' : '#a78bfa';
                  const paneBg = engine === 'omni-flash' ? 'rgba(96,165,250,0.2)' : 'rgba(167,139,250,0.2)';
                  const paneText = engine === 'omni-flash' ? 'text-blue-300/80' : 'text-violet-300/80';
                  const paneChipBg = engine === 'omni-flash' ? 'bg-blue-500/20 text-blue-200' : 'bg-violet-500/20 text-violet-200';
                  const paneAccent = engine === 'omni-flash' ? 'text-blue-300' : 'text-violet-300';
                  const engineLabel = engine === 'omni-flash' ? 'Gemini Omni Flash' : 'Runway Aleph';
                  const durationHint = engine === 'omni-flash'
                    ? 'Only the first 10s are used.'
                    : 'Only the first 5s are used.';
                  return (
                    <div
                      className="mb-4 rounded-lg overflow-hidden border-2 transition-all"
                      style={{
                        borderColor: isR0Selected ? paneColor : `${paneColor}55`,
                        boxShadow: isR0Selected ? `0 0 20px ${paneColor}59` : 'none',
                      }}
                      data-testid="video-workbench-source-video"
                    >
                      {/* Chrome (header) is the click target for
                       *  R0-selection so the video controls stay fully
                       *  interactive (previously the outer div's onClick
                       *  + cursor:pointer intercepted play-button clicks
                       *  on some browsers, giving a "grayed-out" feel). */}
                      <div
                        className="flex items-center justify-between px-3 py-1.5 border-b border-white/5 cursor-pointer"
                        style={{ background: 'rgba(0,0,0,0.4)' }}
                        onClick={() => setSelectedVideoUrl(r0Url)}
                      >
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${paneText} flex items-center gap-1.5`}>
                          <span className={`px-1.5 py-0.5 rounded ${paneChipBg}`}>R0 · Source</span>
                          <Film className="w-3 h-3" /> original
                        </span>
                        <span className="text-[9px] text-white/30 flex items-center gap-1.5">
                          {(r0Engine || shot?.video_engine) ? `via ${r0Engine || shot.video_engine}` : ''}
                          {isR0Selected && <Check className={`w-3 h-3 ${paneAccent}`} />}
                        </span>
                      </div>
                      <div className="bg-black">
                        <video
                          key={resolveUrl(r0Url)}
                          src={resolveUrl(r0Url)}
                          className="w-full object-contain"
                          style={{ maxHeight: '35vh' }}
                          controls
                          preload="auto"
                          playsInline
                          poster={resolveUrl(keyframeUrl)}
                          data-testid="video-workbench-source-video-player"
                          onPlay={() => setSelectedVideoUrl(r0Url)}
                        />
                      </div>
                      <p className="px-3 py-1.5 text-[9px] text-white/30 leading-snug" style={{ background: 'rgba(0,0,0,0.3)' }}>
                        {engineLabel} reads this source video for <span className={paneAccent}>motion, composition, and cast</span>.
                        Your prompt drives what changes. {durationHint}
                      </p>
                    </div>
                  );
                })()}

                {/* Anchor Frame Iterator panel (2026-02 new feature).
                 *  Renders the gpt-image-2 painter for Aleph's identity
                 *  anchor: extract frame 0 of the source video, edit it
                 *  with prompts, pick one. The selected anchor becomes
                 *  the dominant reference Runway uses for the v2v output.
                 *  Only meaningful on Runway (other engines are i2v, not
                 *  v2v — they take the static keyframe). */}
                {isV2V && (anchorR0Url || anchorIterations.length > 0) && (
                  <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.15)' }}>
                    <AnchorFrameIterator
                      mode="shot"
                      shotId={shotId}
                      r0Url={anchorR0Url}
                      iterations={anchorIterations}
                      selectedUrl={selectedAnchorUrl}
                      onSelect={setSelectedAnchorUrl}
                      onIterationsChanged={setAnchorIterations}
                      disabled={generating}
                    />
                  </div>
                )}

                {/* Source Keyframe — shown only for image-to-video engines
                 *  (Kling / Veo / Seedance). For v2v engines (Aleph, Omni)
                 *  the SOURCE VIDEO pane above supersedes this. */}
                {keyframeUrl && !isV2V && (
                  <div className="mb-4">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/30 mb-1.5 block">
                      Source Keyframe
                    </span>
                    <div className="rounded-lg overflow-hidden border-2 border-white/10 mx-auto">
                      <img
                        src={resolveUrl(keyframeUrl)}
                        alt="Keyframe"
                        className="w-full object-contain select-none"
                        style={{ maxHeight: iterations.length > 0 ? '25vh' : '45vh' }}
                        draggable={false}
                        data-testid="video-workbench-keyframe-img"
                      />
                    </div>
                  </div>
                )}

                {/* Generated Video Iterations — stacked, latest on top */}
                {iterations.length > 0 && (
                  <div className="space-y-3">
                    {[...iterations].reverse().map((iter, revIdx) => {
                      const actualIdx = iterations.length - 1 - revIdx;
                      const iterEngine = ENGINES.find(e => e.id === iter.engine) || ENGINES[0];
                      const isSelected = iter.video_url === selectedVideoUrl;
                      return (
                        <div key={actualIdx} className="rounded-xl overflow-hidden border-2 transition-all cursor-pointer"
                          style={{
                            borderColor: isSelected ? iterEngine.color : 'rgba(255,255,255,0.08)',
                            boxShadow: isSelected ? `0 0 20px ${iterEngine.bg}` : 'none',
                          }}
                          onClick={() => setSelectedVideoUrl(iter.video_url)}
                          data-testid={`video-workbench-iteration-${actualIdx}`}
                        >
                          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5" style={{ background: 'rgba(0,0,0,0.4)' }}>
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider"
                              style={{ background: iterEngine.bg, color: iterEngine.color }}>
                              {iterEngine.label}
                            </span>
                            <span className="text-[10px] font-bold text-white/50">R{iter.round}</span>
                            <span className="text-[9px] text-white/30">{iter.duration}s</span>
                            {/* Feb 2026 — symmetric V2V fallback pill.
                             *  Backend sets `fallback_from` when Aleph⇄Omni
                             *  auto-recovers from a hard content-policy block. */}
                            {iter.fallback_from && (
                              <span
                                className="text-[8px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30"
                                title={`Primary engine (${iter.fallback_from}) failed — auto-fell back to ${iter.engine}`}
                                data-testid={`video-workbench-fallback-pill-${actualIdx}`}
                              >
                                ↻ auto-fell back
                              </span>
                            )}
                            {isSelected && <Check className="w-3 h-3 ml-auto" style={{ color: iterEngine.color }} />}
                          </div>
                          <div className="bg-black">
                            <video
                              ref={isSelected ? videoRef : undefined}
                              src={resolveUrl(iter.video_url)}
                              className="w-full object-contain"
                              style={{ maxHeight: '35vh' }}
                              controls
                              preload="metadata"
                              poster={resolveUrl(keyframeUrl)}
                              data-testid={`video-workbench-player-${actualIdx}`}
                            />
                          </div>
                          {iter.prompt && (
                            <div className="px-3 py-1.5 text-[9px] text-white/30 truncate" style={{ background: 'rgba(0,0,0,0.3)' }}>
                              "{iter.prompt?.substring(0, 80)}"
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {!keyframeUrl && !iterations.length && (
                  <div className="flex items-center justify-center py-20 text-white/30 text-sm">
                    No keyframe available for this shot
                  </div>
                )}
              </>
            )}
          </div>

          {/* ═══ RIGHT: Controls ═══ */}
          <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-white/10 flex flex-col flex-shrink-0 max-h-[50vh] md:max-h-none" style={{ background: 'rgba(6,18,28,0.6)' }}>
            {error && (
              <div className="px-4 pt-3">
                <div className="p-2.5 rounded-lg text-[10px] text-red-400 border border-red-500/20" style={{ background: 'rgba(239,68,68,0.08)' }}>
                  {error}
                </div>
              </div>
            )}

            <div className="flex-1 flex flex-col p-4 overflow-y-auto gap-4">
              {/* 1. Model Selector — split into two semantic groups:
               *    a) Keyframe → Video (i2v): Seedance, Veo, Kling
               *    b) Video Edit (v2v): Runway Aleph
               *  Aleph is fundamentally a different beast (edits an existing
               *  video instead of generating one from a keyframe), so the UI
               *  surfaces that distinction with a labeled divider. */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold"
                    style={{ background: engineMeta.bg, color: engineMeta.color }}>1</div>
                  <span className="text-[10px] font-medium text-white/60 uppercase tracking-wider">Model</span>
                </div>

                {/* Group A — Keyframe → Video */}
                <div className="mb-2">
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-white/30 mb-1.5 flex items-center gap-1.5">
                    <span className="h-px flex-1 bg-white/10" />
                    Keyframe → Video
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                  <div className="grid grid-cols-3 gap-1.5" data-testid="video-workbench-engine-selector-i2v">
                    {ENGINES.filter(e => e.id !== 'runway' && e.id !== 'omni-flash').map((eng) => {
                      const isAvailable = availableEngines[eng.id] !== false;
                      const isLocked = eng.premium && !isPremium;
                      const isActive = engine === eng.id;
                      const disabled = generating || !isAvailable || isLocked;
                      return (
                        <button
                          key={eng.id}
                          onClick={() => !disabled && setEngine(eng.id)}
                          disabled={disabled}
                          title={isLocked ? 'Premium only — upgrade to unlock' : eng.label}
                          className={`relative py-2 rounded-lg text-[10px] font-semibold transition-all border ${
                            isLocked ? 'opacity-40 cursor-not-allowed border-white/5 bg-white/3 text-white/25' :
                            !isAvailable ? 'opacity-30 cursor-not-allowed border-white/5 bg-white/3 text-white/20' :
                            isActive ? 'border-opacity-60' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                          }`}
                          style={isActive && !disabled ? { background: eng.bg, borderColor: eng.border, color: eng.color } : undefined}
                          data-testid={`video-workbench-engine-${eng.id}`}
                        >
                          {eng.label}
                          {isLocked && (
                            <span className="absolute -top-1.5 -right-1.5 text-[7px] px-1 py-0.5 rounded bg-amber-500/80 text-black font-bold uppercase tracking-wider">
                              Pro
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Group B — Video Edit (Aleph) */}
                <div>
                  <div className="text-[9px] font-semibold uppercase tracking-wider text-white/30 mb-1.5 flex items-center gap-1.5">
                    <span className="h-px flex-1 bg-white/10" />
                    Video Edit
                    <span className="h-px flex-1 bg-white/10" />
                  </div>
                  <div className="grid grid-cols-2 gap-1.5" data-testid="video-workbench-engine-selector-v2v">
                    {ENGINES.filter(e => e.id === 'runway' || e.id === 'omni-flash').map((eng) => {
                      const isAvailable = availableEngines[eng.id] !== false;
                      const isLocked = eng.premium && !isPremium;
                      const hasSourceVideo = !!shot?.video_url;
                      const isActive = engine === eng.id;
                      const disabled = generating || !isAvailable || isLocked || !hasSourceVideo;
                      const lockReason = isLocked
                        ? 'Premium only — upgrade to unlock'
                        : !isAvailable
                        ? `${eng.label} not available`
                        : !hasSourceVideo
                        ? `Generate a base video first (Seedance / Veo / Kling), then edit it with ${eng.label}`
                        : eng.label;
                      return (
                        <button
                          key={eng.id}
                          onClick={() => !disabled && setEngine(eng.id)}
                          disabled={disabled}
                          title={lockReason}
                          className={`relative py-2 px-2 rounded-lg text-[10px] font-semibold transition-all border flex items-center justify-center gap-1 ${
                            isLocked ? 'opacity-40 cursor-not-allowed border-white/5 bg-white/3 text-white/25' :
                            !isAvailable ? 'opacity-30 cursor-not-allowed border-white/5 bg-white/3 text-white/20' :
                            !hasSourceVideo ? 'opacity-40 cursor-not-allowed border-white/5 bg-white/3 text-white/30' :
                            isActive ? 'border-opacity-60' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                          }`}
                          style={isActive && !disabled ? { background: eng.bg, borderColor: eng.border, color: eng.color } : undefined}
                          data-testid={`video-workbench-engine-${eng.id}`}
                        >
                          <Film className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{eng.label}</span>
                          {isLocked && (
                            <span className="absolute -top-1.5 -right-1.5 text-[7px] px-1 py-0.5 rounded bg-amber-500/80 text-black font-bold uppercase tracking-wider">
                              Pro
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 2. Settings */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold"
                    style={{ background: engineMeta.bg, color: engineMeta.color }}>2</div>
                  <span className="text-[10px] font-medium text-white/60 uppercase tracking-wider">Settings</span>
                </div>

                {/* Duration */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-white/50">Duration</span>
                    <span className="text-[10px] font-mono" style={{ color: engineMeta.color }}>
                      {Math.max(durationRange.min, Math.min(durationRange.max, duration))}s
                    </span>
                  </div>
                  <input
                    type="range" min={durationRange.min} max={durationRange.max} step={durationRange.step}
                    value={Math.max(durationRange.min, Math.min(durationRange.max, duration))}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className="w-full h-1"
                    style={{ accentColor: engineMeta.color }}
                    disabled={generating}
                    data-testid="video-workbench-duration-slider"
                  />
                  <div className="flex justify-between text-[8px] text-white/20 mt-0.5">
                    <span>{durationRange.min}s</span><span>{durationRange.max}s</span>
                  </div>
                </div>

                {/* Mode (HappyHorse + Kling) */}
                {(engine === 'happyhorse' || engine === 'kling') && (
                  <div className="flex gap-1.5 mb-3" data-testid="video-workbench-mode-toggle">
                    {['pro', 'std'].map((m) => (
                      <button key={m} onClick={() => setMode(m)} disabled={generating}
                        className={`flex-1 py-1.5 rounded-lg text-[10px] font-semibold transition-all border ${
                          mode === m ? '' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
                        }`}
                        style={mode === m ? { background: engineMeta.bg, borderColor: engineMeta.border, color: engineMeta.color } : undefined}
                        data-testid={`video-workbench-mode-${m}`}
                      >
                        {m === 'pro' ? 'Pro' : 'Standard'}
                      </button>
                    ))}
                  </div>
                )}

                {/* Sound + CFG (HappyHorse only) */}
                {engine === 'happyhorse' && (
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => setSound(!sound)} disabled={generating}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all border"
                      style={sound ? { background: engineMeta.bg, borderColor: engineMeta.border, color: engineMeta.color }
                        : { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }}
                      data-testid="video-workbench-sound-toggle"
                    >
                      {sound ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
                      Audio {sound ? 'On' : 'Off'}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[9px] text-white/40">CFG</span>
                        <span className="text-[9px] font-mono" style={{ color: engineMeta.color }}>{cfgScale.toFixed(1)}</span>
                      </div>
                      <input
                        type="range" min={0} max={1} step={0.1} value={cfgScale}
                        onChange={(e) => setCfgScale(Number(e.target.value))}
                        className="w-full h-1"
                        style={{ accentColor: engineMeta.color }}
                        disabled={generating}
                        data-testid="video-workbench-cfg-slider"
                      />
                    </div>
                  </div>
                )}

                {/* Kling Motion Control */}
                {engine === 'kling' && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-white/50 flex items-center gap-1">
                        <Film className="w-3 h-3" /> Motion Reference (optional)
                      </span>
                    </div>
                    {motionPreview ? (
                      <div className="relative rounded-lg overflow-hidden border border-rose-500/30">
                        <video src={motionPreview} className="w-full max-h-24 object-contain bg-black" controls muted />
                        <button
                          onClick={() => { setMotionFile(null); setMotionPreview(null); }}
                          className="absolute top-1 right-1 p-1 rounded-full bg-black/70 hover:bg-red-500/30 transition-colors"
                          data-testid="video-workbench-motion-remove"
                        >
                          <X className="w-3 h-3 text-white/70" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => motionInputRef.current?.click()}
                        disabled={generating}
                        className="w-full py-3 rounded-lg border border-dashed border-white/15 text-[10px] text-white/40 hover:border-rose-500/40 hover:text-rose-400 transition-all flex items-center justify-center gap-2"
                        data-testid="video-workbench-motion-upload"
                      >
                        <Upload className="w-3.5 h-3.5" /> Upload reference video
                      </button>
                    )}
                    <input
                      ref={motionInputRef}
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={handleMotionFile}
                    />
                  </div>
                )}
                {/* Runway Aleph references — Aleph receives:
                 *    1. SOURCE VIDEO (`videoUri`) — motion + composition
                 *    2. KEYFRAME (refs[0]) — identity anchor (locked)
                 *    3. OPERATOR REFS (refs[1..2]) — optional style hints
                 *
                /* Runway Aleph references — operator-uploaded only.
                 * Aleph reads the SOURCE VIDEO natively for motion +
                 * composition + cast, so we no longer auto-lock the keyframe
                 * as an identity anchor (that was an artifact of the earlier
                 * Google Omni integration which silently ignored video input).
                 * Refs are now purely style hints — 0 to 3, all operator's
                 * choice. Matches the standalone /recipes/runway-aleph UX. */}
                {/* Style references — shown for both v2v engines (Aleph and
                 * Omni Flash). Both engines take optional style/identity
                 * anchors (Aleph natively via `keyframes`, Omni Flash
                 * folded into the prompt as visual descriptors). */}
                {isV2V && (
                  <div className="mb-3" data-testid="video-workbench-runway-refs">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-white/50 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Style references <span className="text-white/25 normal-case font-normal">· optional</span>
                      </span>
                      <span className="text-[9px] text-white/30">{omniRefs.length}/3</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[0, 1, 2].map((slot) => {
                        const r = omniRefs[slot];
                        if (r) {
                          return (
                            <div key={slot} className="relative rounded-md overflow-hidden border border-violet-500/30 aspect-square">
                              <img src={r.preview} alt={`ref ${slot + 1}`} className="w-full h-full object-cover" />
                              <button
                                onClick={() => setOmniRefs((cur) => cur.filter((_, i) => i !== slot))}
                                className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/70 hover:bg-red-500/30 transition-colors"
                                data-testid={`video-workbench-runway-ref-remove-${slot}`}
                                disabled={generating}
                              >
                                <X className="w-2.5 h-2.5 text-white/80" />
                              </button>
                              <span className="absolute bottom-0.5 left-0.5 px-1 py-0.5 rounded text-[7px] uppercase font-bold tracking-wide bg-white/10 text-white/70">
                                Ref {slot + 1}
                              </span>
                            </div>
                          );
                        }
                        const canAdd = !generating && omniRefs.length === slot;
                        return (
                          <button
                            key={slot}
                            onClick={() => canAdd && omniRefInputRef.current?.click()}
                            disabled={!canAdd}
                            className="rounded-md border border-dashed border-white/15 text-white/40 hover:border-violet-500/40 hover:text-violet-400 transition-all flex flex-col items-center justify-center gap-0.5 aspect-square disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-white/15 disabled:hover:text-white/40"
                            data-testid={`video-workbench-runway-ref-add-${slot}`}
                          >
                            <Upload className="w-3.5 h-3.5" />
                            <span className="text-[8px]">+ ref</span>
                          </button>
                        );
                      })}
                    </div>
                    <input
                      ref={omniRefInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const room = 3 - omniRefs.length;
                        const accepted = files.slice(0, room).map((f) => ({
                          file: f,
                          preview: URL.createObjectURL(f),
                        }));
                        setOmniRefs((cur) => [...cur, ...accepted]);
                        if (e.target) e.target.value = '';
                      }}
                    />
                    <p className="mt-1 text-[9px] text-white/40 leading-snug">
                      Runway Aleph reads the <span className="text-violet-300">source video</span> directly for motion, composition, and cast.
                      Add up to 3 optional refs to hint color / wardrobe / mood shifts.
                    </p>
                  </div>
                )}
              </div>

              {/* 3. Prompt */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${
                    prompt.trim() ? '' : 'bg-white/10 text-white/30'
                  }`}
                    style={prompt.trim() ? { background: engineMeta.bg, color: engineMeta.color } : undefined}
                  >3</div>
                  <span className="text-[10px] font-medium text-white/60 uppercase tracking-wider">Prompt</span>
                </div>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the motion and scene..."
                  className="w-full h-24 text-xs px-3 py-2.5 text-white rounded-xl border border-white/10 outline-none resize-none transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)', focusBorderColor: engineMeta.border }}
                  disabled={generating}
                  data-testid="video-workbench-prompt-input"
                />
              </div>

              {/* 4. Character Lock (HappyHorse only) */}
              {engine === 'happyhorse' && castCandidates.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold"
                      style={useCharacterLock ? { background: engineMeta.bg, color: engineMeta.color } : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }}
                    >4</div>
                    <span className="text-[10px] font-medium text-white/60 uppercase tracking-wider">Character Lock</span>
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer mb-2" data-testid="video-workbench-character-lock">
                    <input
                      type="checkbox"
                      checked={useCharacterLock}
                      onChange={(e) => setUseCharacterLock(e.target.checked)}
                      disabled={generating}
                      className="mt-0.5"
                      style={{ accentColor: engineMeta.color }}
                    />
                    <span className="text-[10px] text-white/60 leading-relaxed">
                      Use cast mugshots as HappyHorse elements (max 3)
                    </span>
                  </label>
                  {useCharacterLock && (
                    <div className="flex gap-1.5 flex-wrap">
                      {castCandidates.slice(0, 3).map((c, i) => (
                        <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-lg border"
                          style={{ borderColor: engineMeta.border, background: engineMeta.bg }}>
                          {(c.url || c.mugshot_url) && (
                            <img src={resolveUrl(c.mugshot_url || c.url)} alt="" className="w-5 h-5 rounded-full object-cover" />
                          )}
                          <span className="text-[9px] font-medium" style={{ color: engineMeta.color }}>
                            {c.name || c.character_name || `Cast ${i+1}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Generate Button */}
              <button
                onClick={handleGenerate}
                disabled={!canGenerate}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-30"
                style={{
                  background: generating ? engineMeta.bg : `linear-gradient(135deg, ${engineMeta.color}, ${engineMeta.color}dd)`,
                  color: 'white',
                }}
                data-testid="video-workbench-generate-btn"
              >
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {generating
                  ? (isV2V ? 'Editing...' : 'Generating...')
                  : (isV2V ? `Edit with ${engineMeta.label}` : `Generate with ${engineMeta.label}`)}
              </button>

              {/* Credits / Iteration Info */}
              {iterations.length > 0 && (
                <div className="mt-1 flex items-center gap-2 p-2 rounded-lg border"
                  style={{ borderColor: `${engineMeta.color}33`, background: engineMeta.bg }}>
                  <div className="w-2 h-2 rounded-full" style={{ background: engineMeta.color }} />
                  <span className="text-[10px]" style={{ color: `${engineMeta.color}cc` }}>
                    {iterations.length} round{iterations.length > 1 ? 's' : ''} ·
                    {[...new Set(iterations.map(i => i.engine))].map(e => ENGINES.find(x => x.id === e)?.label || e).join(', ')}
                  </span>
                </div>
              )}
            </div>

            {/* Accept / Revert */}
            {iterations.length > 0 && (
              <div className="border-t border-white/10 p-4 space-y-2">
                <button
                  onClick={handleAccept}
                  disabled={!selectedVideoUrl || generating}
                  className="w-full py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 disabled:opacity-30"
                  style={{ background: `linear-gradient(135deg, ${engineMeta.color}, ${engineMeta.color}dd)`, color: 'white' }}
                  data-testid="video-workbench-accept-btn"
                >
                  <Check className="w-3.5 h-3.5" />
                  Accept Video
                </button>
                <button
                  onClick={handleRevert}
                  disabled={generating}
                  className="w-full py-2 rounded-xl text-[11px] font-medium transition-all flex items-center justify-center gap-1.5 border border-red-500/30 text-red-400/80 hover:bg-red-500/10"
                  data-testid="video-workbench-revert-btn"
                >
                  <RotateCcw className="w-3 h-3" />
                  Revert & Clear All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? ReactDOM.createPortal(content, document.body) : null;
}
